import { useState } from "react";
import { DateRangePicker, Range } from "react-date-range";
import {
  addDays,
  endOfDay,
  startOfDay,
  format,
  differenceInDays,
  isSameDay,
  isSameYear,
  isSameMonth,
} from "date-fns";

import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file
import { default as Modal } from "../../../components/Modal";
import _ from "lodash";
import { IBaseInputProps } from "../../../components/Forms/Inputs";

interface PredefinedRange {
  days: number;
  label?: string;
}

export interface IDateRange {
  startDate: Date;
  endDate: Date;
}

// interface DateRangeProps {
//   onChange: (props: any) => any;
//   currentRange: IDateRange;
//   changed?: boolean;
//   limitDays?: number;
//   disabled?: boolean;
// }

const matchDates = (d1: Date | undefined, d2: Date | undefined) => {
  if (!d1 || !d2) return false;
  const date1 = d1.getFullYear() * 10000 + d1.getMonth() * 100 + d1.getDate();
  const date2 = d2.getFullYear() * 10000 + d2.getMonth() * 100 + d2.getDate();
  return date1 === date2;
};

const setStaticRanges = (ranges: Array<PredefinedRange>, cur: Range) => {
  return ranges.map((r) => {
    const display = r.label || r.days.toString() + " days including today";
    const end = endOfDay(new Date());
    const start = addDays(startOfDay(new Date()), 1 - r.days);
    const selected =
      matchDates(cur.startDate, start) && matchDates(cur.endDate, end);

    return {
      label: display,
      range: () => ({
        startDate: start,
        endDate: end,
      }),
      isSelected() {
        return selected;
      },
    };
  });
};

export const getRangeAsLabels = (
  range: IDateRange | undefined,
  withDays: boolean = true
) => {
  if (!range) return "No dates selected";
  if (isSameDay(range.startDate, range.endDate))
    return format(range.startDate, "d MMMM u");
  const numDays = withDays
    ? ` (${differenceInDays(range.endDate, range.startDate) + 1} days)`
    : "";
  if (isSameMonth(range.startDate, range.endDate))
    return (
      format(range.startDate, "d") +
      " - " +
      format(range.endDate, "d MMM u") +
      numDays
    );
  if (isSameYear(range.startDate, range.endDate))
    return (
      format(range.startDate, "d MMM") +
      " - " +
      format(range.endDate, "d MMM u") +
      numDays
    );
  return (
    format(range.startDate, "d MMM u") +
    " - " +
    format(range.endDate, "d MMM u") +
    numDays
  );
};

export const getTotalDaysAsString = (range: IDateRange) => {
  return (differenceInDays(range.endDate, range.startDate) + 1).toString();
};

// export const convertRangeToStringArray = (range: IDateRange): string[] => {
//   return [
//     format(range.startDate, "yyyy-MM-dd"),
//     format(range.endDate, "yyyy-MM-dd"),
//   ];
// };

// export const convertStringArrayToRange = (
//   range: any
// ): IDateRange | undefined => {
//   const useValue = _.isArray(range) ? range : [];
//   if (useValue.length !== 2) return undefined;
//   return {
//     startDate: new Date(useValue[0]),
//     endDate: new Date(useValue[1]),
//   };
// };

const customInputRange = [
  {
    label: "days from today",
    range(value: number) {
      return {
        startDate: startOfDay(new Date()),
        endDate: endOfDay(addDays(new Date(), value - 1)),
        key: "selection",
      };
    },
    getCurrentValue(range: any) {
      if (!isSameDay(range.startDate, startOfDay(new Date()))) return "-";
      if (!range.endDate) return "∞";
      return differenceInDays(range.endDate, range.startDate) + 1;
    },
  },
];

// const DateRangeSelector = ({
//   onChange,
//   currentRange,
//   changed,
//   limitDays = 14,
//   disabled = false,
// }: DateRangeProps) => {

const DateRangeSelector = ({
  onChange,
  disabled = false,
  value,
}: IBaseInputProps) => {
  const nowRange = {
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  };

  //const currentRange = convertStringArrayToRange(value);

  // Date range is what is used by the date selector as a display
  // This should be updated with the current shown range
  const [dateRange, setDateRange] = useState<IDateRange>(value || nowRange);

  // toggle state for the date picker modal
  const [modalOpen, setModalOpen] = useState(false);

  // Classes
  const today = startOfDay(new Date());
  const bottomBtnClass =
    "w-1/2 h-10 px-4 text-sm font-bold bg-gray-100 hover:bg-gray-300 text-gray-600 border-gray-400 uppercase disabled:pointer-events-none disabled:text-gray-400 disabled:bg-gray-300";
  const cancelBtnClass = bottomBtnClass + " rounded-br-lg border-t border-l";
  const applyBtnClass = bottomBtnClass + " rounded-bl-lg border-t";

  // handler for date selector changing dates
  // ----
  const handleRangeChange = (item: any) => {
    const dateObject = item.selection
      ? item.selection
      : item.range1
      ? {
          startDate: item.range1.startDate,
          endDate: item.range1.endDate,
        }
      : null;

    if (dateObject === null) return;

    const startDate = dateObject.startDate || dateObject.endDate;
    const endDate = endOfDay(dateObject.endDate || dateObject.startDate);
    setDateRange({
      ...dateObject,
      startDate: startDate,
      endDate: endDate,
    });
  };

  // Predefined handler
  // -- this should update the shownRange AND dateRange --
  const rangeArray = [
    { days: 1, label: "Today only" },
    { days: 7, label: "Past 7 days" },
    { days: 14, label: "Past 2 weeks" },
    { days: 28, label: "Past 4 weeks" },
    { days: 84, label: "Past 12 weeks" },
  ];

  // Handle btn click applies to both cancel and toggle
  // -- This should open the selector modal, close the menu,
  // and have the date range updated --
  const handleModalBtnClicked = () => {
    setModalOpen(!modalOpen);
    if (!dateRange) setDateRange(nowRange);
  };

  // Custom range handler
  // -- Updates the shown range from the date range --
  const handleClickApply = () => {
    setModalOpen(false);
    if (!dateRange) return;
    if (
      !matchDates(dateRange.startDate, value?.startDate) ||
      !matchDates(dateRange.endDate, value?.endDate)
    ) {
      const value = dateRange;
      const obj = { target: { value } };
      onChange(obj);
    }
  };

  const outsideRange =
    dateRange && differenceInDays(dateRange.endDate, dateRange.startDate) > 120;

  return (
    <>
      <button
        className={`min-w-[12rem] border border-[#ccc] rounded wrap-none flex justify-between hover:border-[#aaa] py-2`}
        name="DateRangeSelect"
        onClick={(e) => {
          e.preventDefault();
          setModalOpen(!modalOpen);
        }}
        disabled={disabled}
      >
        <div className="p-0.5 px-2.5 text-xs text-gray-700">
          {getRangeAsLabels(value)}
        </div>
        <i className="text-sm icon-edit px-3 border-l border-[#ccc] text-[#bbb] hover:font-bold hover:text-[#999]" />
      </button>
      <Modal setShowExternal={setModalOpen} showModal={modalOpen}>
        <Modal.Display tailwindWidth="w-fit">
          <div className="w-auto rounded-t-lg bg-gray-100 h-fit border-b border-gray-400 p-4 space-y-3">
            <div className="font-bold text-gray-700">Select a date range</div>
            <div className="text-xs max-w-prose text-gray-700 space-y-2">
              <p>
                Select a range of up to 120 days to fetch analytics data from by
                selecting the dates from the calendar, or entering them in the
                inputs below.
              </p>
            </div>
          </div>
          <div className="w-fit inline-block">
            <DateRangePicker
              onChange={(item) => handleRangeChange(item)}
              showPreview={true}
              moveRangeOnFirstSelection={false}
              months={1}
              maxDate={today}
              ranges={[dateRange]}
              direction="vertical"
              fixedHeight={true}
              staticRanges={setStaticRanges(rangeArray, dateRange)}
              inputRanges={customInputRange}
              editableDateInputs={true}
              dateDisplayFormat="d MMM yyyy"
              startDatePlaceholder="Select start date"
              endDatePlaceholder="Select end date"
            />
          </div>
          <div id="bottomButtons" className="w-full">
            <button className={applyBtnClass} onClick={handleModalBtnClicked}>
              Cancel
            </button>
            <button
              className={cancelBtnClass}
              onClick={handleClickApply}
              disabled={outsideRange}
            >
              {outsideRange ? (
                <span className="text-warningRed text-xs normal-case">
                  Maximum 120 days
                </span>
              ) : (
                `Apply`
              )}
            </button>
          </div>
        </Modal.Display>
      </Modal>
    </>
  );
};

DateRangeSelector.defaultProps = {
  swapAlign: false,
};

export default DateRangeSelector;
